<template>
  <div class="py-10">
    <div class="flex items-center space-x-4 shrink grow-0 mb-10">
      <Spinner v-if="!currentShow" />
      <template v-else>
        <picture class="object-cover">
          <source :srcset="currentShow.srcset.sqSm.src" type="image/webp" />
          <img
            :src="currentShow.srcset.original.src"
            :alt="currentShow.name"
            class="rounded-full h-16 w-16"
          />
        </picture>
        <span class="text-xl font-heading font-bold" id="title-text">
          {{ currentShow.name }}
        </span>
      </template>
    </div>
    <section
      class="flex flex-col space-y-5 md:flex-row md:space-y-0 md:items-start md:justify-start md:space-x-8"
    >
      <div class="md:w-1/5">
        <span class="uppercase font-label text-fg-secondary text-sm inline-block mb-2"
          >Floor Plan</span
        >
        <SelectList
          :options="arrangements"
          itemText="name"
          :value="performanceForm.arrangement"
          @change="performanceForm.arrangement = $event"
        />
      </div>
      <div class="md:w-1/5">
        <span class="uppercase font-label text-fg-secondary text-sm inline-block mb-2"
          >Seating Time Set</span
        >
        <SelectList
          :options="showtimes"
          itemText="display"
          :value="performanceForm.showtime"
          @change="performanceForm.showtime = $event"
        />
      </div>
      <div class="md:w-96">
        <span class="uppercase font-label text-fg-secondary text-sm inline-block mb-2">
          Performance Date
        </span>
        <DatePicker
          class="mb-10"
          :selectedDate="performanceForm.date"
          :invalidDates="['before-today']"
          @datePickerChange="performanceForm.date = $event.date"
        />
      </div>
      <div class="md:w-60">
        <DateTimePicker
          v-model:isOpen="isPublishDatePickerOpen"
          v-model:selectedDate="performanceForm.publish_date"
          v-model:selectedTime="performanceForm.publish_time"
          label="Published Date"
          :invalidDates="['before-today']"
          :datetimeLabel="publishedDateLabel"
        />

        <span class="uppercase font-label text-fg-secondary text-sm inline-block mt-6">
          Unpublished Date
        </span>
        <span class="block" id="unpublished-date-label">
          {{ unpublishedDateLabel }}
        </span>

        <div v-if="!isSaving" class="flex space-x-4 justify-end mt-10 md:mt-72" id="button-footer">
          <ButtonDefault
            classes="w-24"
            label="Discard"
            @click="goToShowDetail"
            id="discard-button"
            alt
          />
          <ButtonDefault
            classes="w-24"
            label="Save"
            @click="savePerformance"
            :disabled="!isFormValid"
            id="save-button"
          />
        </div>
        <div v-else class="flex flex-col space-y-1 mt-10 md:mt-72" id="loader">
          <span class="font-sans">Saving...</span>
          <LoaderLine />
        </div>
        <ErrorDisplay v-bind="ui.error" class="p-2" />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import moment from 'moment'
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import LoaderLine from '@/common/components/atoms/LoaderLine.vue'
import Spinner from '@/common/components/atoms/Spinner.vue'
import SelectList from '@/common/components/molecules/SelectList.vue'
import DatePicker from '@/common/components/organisms/DatePicker.vue'
import DateTimePicker from '@/common/components/organisms/DateTimePicker.vue'
import { StoreFloorPlan, StoreShow, StoreShowTime } from '@/models'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { useFloorPlanStore } from '@/stores/floorplan'
import { useSettingStore } from '@/stores/setting'
import { useShowStore } from '@/stores/show'
import { useShowTimeStore } from '@/stores/showtime'
import { blankError, displayHandled } from '@/utils/errors'
import { currentTime, todayIsoStr } from '@/utils/utils'

import { PerformanceCreate } from '@generated/types'

export default defineComponent({
  name: 'PerformanceAdd',
  components: {
    DateTimePicker,
    ErrorDisplay,
    LoaderLine,
    SelectList,
    ButtonDefault,
    DatePicker,
    Spinner,
  },
  data() {
    return {
      currentShow: null as StoreShow | null,
      performanceForm: {
        arrangement: {} as StoreFloorPlan,
        showtime: {} as StoreShowTime,
        date: todayIsoStr(),
        publish_date: todayIsoStr(),
        publish_time: '00:00',
      },
      isPublishDatePickerOpen: false,
      isSaving: false,
      ui: {
        error: blankError(),
      },
    }
  },
  methods: {
    async goToShowDetail() {
      await this.$router.push({ name: 'showDetail', params: { id: this.$route.params.showId } })
    },
    async savePerformance() {
      if (!this.isFormValid) return
      try {
        this.isSaving = true
        const performanceCreate = this.getPerformanceCreate()
        await this.performanceStore.createPerformance(performanceCreate)
        await this.goToShowDetail()
      } catch (e) {
        this.isSaving = false
        displayHandled(e, this.ui.error)
      }
    },
    getPerformanceCreate(): PerformanceCreate {
      if (!this.currentShow) throw new Error('No show selected')
      return {
        arrangement_id: this.performanceForm.arrangement.id,
        showtime_id: this.performanceForm.showtime.id,
        show_id: this.currentShow.id,
        datetime: this.settingStore.makeDateTime({
          date: this.performanceForm.date,
          time: this.settingStore.time24Str(this.performanceForm.showtime.time),
        }),
        publish_datetime: this.settingStore.makeDateTime({
          date: this.performanceForm.publish_date,
          time: this.performanceForm.publish_time,
        }),
      }
    },
  },
  async mounted() {
    if (this.showId > 0) {
      const [show] = await Promise.all([
        this.showStore.fetchShow(this.showId),
        this.floorPlanStore.fetchAll(),
        this.showtimeStore.fetchAll(),
      ])
      this.currentShow = { ...show }
    } else await this.$router.push({ name: 'showList' })
  },
  computed: {
    ...mapStores(
      useShowStore,
      useFloorPlanStore,
      useSettingStore,
      useShowTimeStore,
      usePerformanceStore,
    ),
    showId(): number {
      const showIdParam: string = (this.$route.params.showId as string) || '0'
      return isNaN(+showIdParam) ? 0 : parseInt(showIdParam)
    },
    arrangements(): StoreFloorPlan[] {
      return this.floorPlanStore.arrangements
        .slice()
        .sort((a, b) => a.name?.localeCompare(b.name || '') || 0)
    },
    showtimes(): StoreShowTime[] {
      return this.showtimeStore.showtimes.slice().sort((a, b) => a.display.localeCompare(b.display))
    },
    isFormValid(): boolean {
      return this.performanceForm.arrangement.id > 0 && this.performanceForm.showtime.id > 0
    },
    unpublishedDateLabel(): string {
      if (this.performanceForm.showtime.time24) {
        return this.settingStore.dateTimeStr(
          this.settingStore.makeDateTime({
            date: this.performanceForm.date,
            time: this.performanceForm.showtime.time24,
          }),
        )
      } else {
        return this.settingStore.formatTemporal(this.performanceForm.date, 'MMMM D, YYYY')
      }
    },
    publishedDateLabel(): string {
      const immediateTime = this.settingStore.makeDateTime({
        date: todayIsoStr(),
        time: currentTime(),
      })
      const publishTime = this.settingStore.makeDateTime({
        date: this.performanceForm.publish_date,
        time: this.performanceForm.publish_time,
      })

      if (moment(publishTime).isSameOrBefore(immediateTime)) {
        return 'Immediately'
      } else {
        return this.settingStore.dateTimeStr(
          this.settingStore.makeDateTime({
            date: this.performanceForm.publish_date,
            time: this.performanceForm.publish_time,
          }),
        )
      }
    },
  },
})
</script>
